import { ErrorDeApi, pedir } from './api';
import { getDeviceId } from './session';
import { claveABytes } from '../features/notificaciones-texto';

/** Notificaciones push: el permiso, la suscripción del navegador y el servidor. */

export type EstadoPushServidor = {
  disponible: boolean;
  clavePublica: string | null;
  preferencias: {
    recordatorios: boolean;
    circulo: boolean;
    finDelFoco: boolean;
    /** Las subidas de nivel y el rewind del año. */
    progreso: boolean;
    discretas: boolean;
  };
  suscrito: boolean;
};

const d = () => ({ deviceId: getDeviceId() });

/** Lo que este navegador puede hacer con los avisos, y lo que ya ha dicho el usuario. */
export function entornoPush() {
  const soportado =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const instalada =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const permiso: NotificationPermission | 'no-soportado' =
    'Notification' in window ? Notification.permission : 'no-soportado';
  return { soportado, ios, instalada, permiso };
}

export function leerPush(): Promise<EstadoPushServidor> {
  return pedir<EstadoPushServidor>('/push', d());
}

/** El registro del service worker, sin colgarse si no lo hay (dev sin SW). */
async function registro(): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, rechazar) =>
      setTimeout(() => rechazar(new Error('sin service worker')), 5000),
    ),
  ]);
}

async function enviarSuscripcion(sub: PushSubscription): Promise<void> {
  const j = sub.toJSON();
  await pedir('/push/suscripcion', {
    ...d(),
    metodo: 'POST',
    cuerpo: { endpoint: j.endpoint, p256dh: j.keys?.['p256dh'], auth: j.keys?.['auth'] },
  });
}

/** Se llama al pulsar el interruptor: el permiso se pide aquí, nunca al abrir la pantalla. */
export async function activarPush(clavePublica: string): Promise<'activadas' | 'bloqueadas'> {
  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') return 'bloqueadas';
  const reg = await registro();
  const clave = claveABytes(clavePublica);
  let sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: clave }));
  try {
    await enviarSuscripcion(sub);
  } catch (error) {
    // Una suscripción vieja de este navegador, de otra cuenta: se rehace.
    if (!(error instanceof ErrorDeApi)) throw error;
    await sub.unsubscribe();
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: clave });
    await enviarSuscripcion(sub);
  }
  return 'activadas';
}

export async function desactivarPush(): Promise<void> {
  try {
    const reg = await registro();
    await (await reg.pushManager.getSubscription())?.unsubscribe();
  } catch {
    // Sin service worker no hay suscripción en el navegador que quitar.
  }
  await pedir('/push/baja', { ...d(), metodo: 'POST', cuerpo: {} });
}

export function guardarPreferenciasPush(
  cambios: Partial<EstadoPushServidor['preferencias']>,
): Promise<EstadoPushServidor['preferencias']> {
  return pedir('/push/preferencias', { ...d(), metodo: 'POST', cuerpo: cambios });
}

export async function enviarPruebaPush(): Promise<boolean> {
  return (await pedir<{ ok: boolean }>('/push/prueba', { ...d(), metodo: 'POST', cuerpo: {} })).ok;
}

/** Programa (con lo que falta) o cancela (`restanteMs: null`) el aviso de fin de un foco. */
export function avisoDeFoco(cuerpo: {
  sesionId: string;
  restanteMs: number | null;
  minutos?: number;
  etiqueta?: string | null;
  descanso?: boolean;
}): Promise<{ programado: boolean }> {
  return pedir('/push/foco', { ...d(), metodo: 'POST', cuerpo });
}
